import type { ProductPreviewCardFragment } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
import Link from 'next/link'
import { linkResolver } from '@/utils/datocms/links'
import CtaTransparent from './ui/cta/CtaTransparent'
import clsx from 'clsx'

type Props = {
	productCard: ProductPreviewCardFragment
	idx: number
}

export default function ProductTilePreviewCard({ productCard, idx }: Readonly<Props>) {
	return (
		<div className="product-tile-card @container">
			<div className="relative group flex flex-col @lg:flex-row gap-4 @lg:gap-10 bg-white-frosted hover:bg-white-frosted-shade rounded-lg p-4 @xl:p-6 h-full transition-colors duration-300 ease-out">
				<Link
					href={linkResolver(productCard)}
					className="absolute inset-0 z-10"
					aria-label={`Learn more about ${productCard.name}`}
				/>

				<div className="rounded-md overflow-hidden aspect-video @lg:aspect-[9/10.5] shrink-0 @lg:w-52 bg-white-frosted-shade">
					<Image
						src={productCard.coverImage?.[0]?.url}
						alt={productCard.coverImage?.[0]?.alt ?? 'Product Tile Image'}
						width={250}
						height={250}
						className={clsx('object-cover w-full h-full', {
							'object-left': productCard.coverImageOrientation === 'left',
							'object-center': productCard.coverImageOrientation === 'center',
							'object-right': productCard.coverImageOrientation === 'right',
						})}
						sizes="(max-width: 512px) 90vw, 208px"
					/>
				</div>
				<div className="flex flex-col h-full">
					<span className="font-mono text-blue text-sm">
						{idx < 10 && '0'}
						{idx + 1}.
					</span>
					<h3 className="heading-size-5 mt-6 line-clamp-3">{productCard.name}</h3>
					{productCard.description && (
						<p className="text-blue-dark/80 @lg:text-lg leading-snug mt-2 line-clamp-2">
							{productCard.description}
						</p>
					)}
					{productCard.productTileDescription && (
						<p className="text-blue-dark/50 leading-snug mt-2 mb-4 line-clamp-3">
							{productCard.productTileDescription}
						</p>
					)}
					<CtaTransparent
						className="mt-auto"
						variant="tertiary"
						cta={{
							__typename: 'NavigationItemRecord',
							id: productCard.id,
							useExternalLink: false,
							openInNewWindow: true,
							internalLink: {
								__typename: 'PageRecord',
								id: productCard.id,
								slug: linkResolver(productCard),
							},
							label: 'Learn More',
						}}
					/>
				</div>
			</div>
		</div>
	)
}
